import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { theme } from "../theme";
import SkeletonCard from "./common/SkeletonCard";
import EmptyState from "./common/EmptyState";
import SummaryCard from "./common/SummaryCard";
import { canSeeMargin } from "../utils/roleUtils";

import { API_BASE } from '../api/config';

function formatCurrency(value, displayCurrency) {
    if (value == null) return "-";
    return `${displayCurrency} ${Math.round(Number(value)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatDate(dateString) {
    if (!dateString) return "-";
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    } catch {
        return dateString;
    }
}

const FOCHeader = React.memo(({ year, fullScreen, onMaximize }) => (
    <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: fullScreen ? "center" : "space-between",
        marginBottom: 8,
    }}>
        <h2 style={{
            color: theme.colors.primary,
            marginTop: 0,
            marginBottom: 0,
            fontSize: fullScreen ? theme.typography.fontSize["2xl"] : theme.typography.fontSize.xl,
            fontWeight: theme.typography.fontWeight.bold,
            letterSpacing: 0.5,
            textAlign: fullScreen ? "center" : "left",
            width: fullScreen ? "100%" : "auto",
        }}>
            FOC Sample Tracker - {year}
        </h2>
        {onMaximize && !fullScreen && (
            <button onClick={onMaximize} style={{
                padding: "8px 14px",
                fontSize: 14,
                background: "linear-gradient(135deg, #111827 0%, #1f2937 100%)",
                color: "#e5e7eb",
                border: "1px solid #4b5563",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: "600",
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                transition: "all 0.2s ease",
            }} title="Maximize" aria-label="Maximize FOC Sample Tracker">⤢</button>
        )}
    </div>
));

const FOCSubTitle = React.memo(({ salesperson, region, fullScreen }) => (
    <p style={{
        color: theme.colors.text.secondary,
        fontSize: fullScreen ? theme.typography.fontSize.base : theme.typography.fontSize.sm,
        marginTop: theme.spacing.xs,
    }}>
        Salesperson: <strong>{salesperson}</strong> · Region:{" "}
        <strong>{region === "all" ? "All Regions" : region}</strong>
    </p>
));

const FOCErrorBox = React.memo(({ error, onRetry }) => {
    if (!error) return null;
    return (
        <div style={{
            padding: theme.spacing.lg,
            background: theme.colors.background.card,
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.colors.danger}`,
            color: theme.colors.danger,
        }}>
            <div style={{ marginBottom: theme.spacing.md }}>
                <strong>Error:</strong> {error}
            </div>
            <button
                onClick={onRetry}
                style={{
                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                    background: theme.colors.danger,
                    color: "#ffffff",
                    border: "none",
                    borderRadius: theme.borderRadius.sm,
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: 14,
                    transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "0.9";
                    e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "1";
                    e.currentTarget.style.transform = "translateY(0)";
                }}
            >
                Retry
            </button>
        </div>
    );
});

function FOCSampleTracker({ filterContext, onMaximize, fullScreen = false }) {
    const { salesperson, year, region, displayCurrency } = filterContext || {};
    const [rows, setRows] = useState([]);
    const [currency, setCurrency] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [sortConfigWon, setSortConfigWon] = useState({ key: "value_aed", direction: "desc" });
    const [sortConfigLost, setSortConfigLost] = useState({ key: "value_aed", direction: "desc" });

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const res = await axios.get(`${API_BASE}/api/metrics/foc-samples`, {
                params: {
                    salesperson,
                    year,
                    region: region || "all",
                },
            });
            const dataRows = res.data?.data || [];
            setRows(dataRows);
            const responseCurrency = res.data?.currency;
            const rowCurrency = Array.isArray(dataRows) && dataRows.length > 0 ? dataRows[0]?.display_currency : "";
            setCurrency(responseCurrency || rowCurrency || displayCurrency || "");
        } catch (err) {
            console.error("FOCSampleTracker error", err);
            const errorMessage = err.response?.data?.detail || err.message || "Failed to load FOC samples";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [salesperson, year, region, displayCurrency]);

    useEffect(() => {
        if (!salesperson || !year) return;
        fetchData();
    }, [salesperson, year, region, fetchData]);

    const normalizeStage = useCallback((stage) => {
        if (!stage) return "";
        return String(stage).trim().toLowerCase();
    }, []);

    const wonRows = useMemo(() => rows.filter(row => normalizeStage(row.stage) === "closed won"), [rows, normalizeStage]);
    const lostRows = useMemo(() => rows.filter(row => normalizeStage(row.stage) === "closed lost"), [rows, normalizeStage]);

    const sortRows = useCallback((rowsToSort, sortConfig) => {
        return [...rowsToSort].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];

            if (sortConfig.key === "value_aed") {
                return sortConfig.direction === "asc"
                    ? (aVal || 0) - (bVal || 0)
                    : (bVal || 0) - (aVal || 0);
            }

            if (sortConfig.key === "margin_pct") {
                return sortConfig.direction === "asc"
                    ? (aVal || 0) - (bVal || 0)
                    : (bVal || 0) - (aVal || 0);
            }

            if (sortConfig.key === "close_date") {
                const aDate = aVal ? new Date(aVal) : new Date(0);
                const bDate = bVal ? new Date(bVal) : new Date(0);
                return sortConfig.direction === "asc"
                    ? aDate - bDate
                    : bDate - aDate;
            }

            const aStr = String(aVal || "").toLowerCase();
            const bStr = String(bVal || "").toLowerCase();
            if (sortConfig.direction === "asc") {
                return aStr.localeCompare(bStr);
            }
            return bStr.localeCompare(aStr);
        });
    }, []);

    const sortedWonRows = useMemo(() => sortRows(wonRows, sortConfigWon), [wonRows, sortConfigWon, sortRows]);
    const sortedLostRows = useMemo(() => sortRows(lostRows, sortConfigLost), [lostRows, sortConfigLost, sortRows]);

    const handleSortWon = useCallback((key) => {
        setSortConfigWon(prev => ({
            key,
            direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc"
        }));
    }, []);

    const handleSortLost = useCallback((key) => {
        setSortConfigLost(prev => ({
            key,
            direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc"
        }));
    }, []);

    const hasData = useMemo(() => rows.length > 0, [rows]);

    const getThStyle = useCallback((align) => {
        return {
            textAlign: align,
            padding: "8px 8px",
            borderBottom: "1px solid rgba(148,163,184,0.4)",
            fontWeight: 600,
            color: "#9ca3af",
            whiteSpace: "nowrap",
            cursor: "pointer",
            userSelect: "none",
        };
    }, []);

    const getSortIcon = useCallback((key, sortConfig) => {
        if (sortConfig.key !== key) return "⇅";
        return sortConfig.direction === "asc" ? "↑" : "↓";
    }, []);

    const showMargin = canSeeMargin();

    const renderTable = useCallback((rowsToRender, sortConfig, handleSort, title, color) => {
        const tableTotal = rowsToRender.reduce((sum, row) => sum + (row.value_aed || 0), 0);
        const tableCurrency = currency || (rowsToRender[0]?.display_currency) || displayCurrency || "";
        return (
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                <h3 style={{
                    color: color,
                    marginTop: 0,
                    marginBottom: 8,
                    fontSize: fullScreen ? 20 : 18,
                    fontWeight: 600,
                    textAlign: fullScreen ? "center" : "left",
                }}>
                    {title} ({rowsToRender.length})
                </h3>

                {/* Summary boxes above each table - 50% width each (25% + 25%) */}
                <div style={{ display: "flex", gap: 8, marginBottom: 12, width: "100%" }}>
                    <div style={{ flex: "1 1 50%", minWidth: 0 }}>
                        <SummaryCard
                            title={`${title} Count`}
                            value={rowsToRender.length}
                            subtitle="samples"
                            color={color}
                            compact={false}
                        />
                    </div>
                    <div style={{ flex: "1 1 50%", minWidth: 0 }}>
                        <SummaryCard
                            title={`${title} Value`}
                            value={formatCurrency(tableTotal, tableCurrency)}
                            subtitle="total"
                            color={color}
                            compact={false}
                        />
                    </div>
                </div>

                {rowsToRender.length === 0 ? (
                    <EmptyState
                        icon=""
                        message={`No ${title} FOC samples found.`}
                        size="small"
                    />
                ) : (
                    <div style={{ marginTop: 8, overflowY: "auto", flex: 1, maxHeight: fullScreen ? "100%" : "600px", paddingRight: 4 }}>
                        <table style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: 13,
                            color: "#e5e7eb",
                        }}>
                            <thead style={{ position: "sticky", top: 0, background: theme.colors.background.card, zIndex: 1 }}>
                                <tr>
                                    <th
                                        style={getThStyle("left")}
                                        onClick={() => handleSort("opportunity_code")}
                                        aria-sort={sortConfig.key === "opportunity_code" ? (sortConfig.direction === "asc" ? "ascending" : "descending") : "none"}
                                        aria-label="Sort by Opportunity Number"
                                    >
                                        Opportunity No {getSortIcon("opportunity_code", sortConfig)}
                                    </th>
                                    <th
                                        style={getThStyle("left")}
                                        onClick={() => handleSort("opportunity_name")}
                                        aria-sort={sortConfig.key === "opportunity_name" ? (sortConfig.direction === "asc" ? "ascending" : "descending") : "none"}
                                        aria-label="Sort by Opportunity Name"
                                    >
                                        Opportunity Name {getSortIcon("opportunity_name", sortConfig)}
                                    </th>
                                    {/* Merged Value/Cost into one column as requested */}
                                    <th
                                        style={{ ...getThStyle("left"), paddingLeft: "30px" }}
                                        onClick={() => handleSort("value_aed")}
                                        aria-sort={sortConfig.key === "value_aed" ? (sortConfig.direction === "asc" ? "ascending" : "descending") : "none"}
                                        aria-label="Sort by Value"
                                    >
                                        Value ({tableCurrency}) {getSortIcon("value_aed", sortConfig)}
                                    </th>
                                    {showMargin && (
                                        <th
                                            style={{ ...getThStyle("left"), paddingLeft: "30px" }}
                                            onClick={() => handleSort("margin_pct")}
                                            aria-sort={sortConfig.key === "margin_pct" ? (sortConfig.direction === "asc" ? "ascending" : "descending") : "none"}
                                            aria-label="Sort by Margin"
                                        >
                                            Margin (%) {getSortIcon("margin_pct", sortConfig)}
                                        </th>
                                    )}
                                    <th
                                        style={{ ...getThStyle("left"), paddingLeft: showMargin ? "60px" : "30px" }}
                                        onClick={() => handleSort("close_date")}
                                        aria-sort={sortConfig.key === "close_date" ? (sortConfig.direction === "asc" ? "ascending" : "descending") : "none"}
                                        aria-label="Sort by Close Date"
                                    >
                                        Close Date {getSortIcon("close_date", sortConfig)}
                                    </th>
                                    <th
                                        style={{ ...getThStyle("left"), paddingLeft: showMargin ? "60px" : "30px" }}
                                        onClick={() => handleSort("br_company")}
                                        aria-sort={sortConfig.key === "br_company" ? (sortConfig.direction === "asc" ? "ascending" : "descending") : "none"}
                                        aria-label="Sort by Region"
                                    >
                                        Region {getSortIcon("br_company", sortConfig)}
                                    </th>
                                    {/* Sales Person Removed as requested */}
                                </tr>
                            </thead>
                            <tbody>
                                {rowsToRender.map((row, idx) => (
                                    <tr key={`foc-${row.opportunity_code}-${idx}`} style={{
                                        backgroundColor: idx % 2 === 0 ? "rgba(15,23,42,0.6)" : "transparent",
                                    }}>
                                        <td style={{ padding: "8px 8px", whiteSpace: "nowrap" }}>
                                            {row.opportunity_code || "-"}
                                        </td>
                                        <td style={{
                                            padding: "8px 8px",
                                            maxWidth: 200,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }} title={row.opportunity_name || ""}>
                                            {row.opportunity_name || "-"}
                                        </td>
                                        <td style={{ padding: "8px 8px", paddingLeft: "30px", textAlign: "left", color: "#fbbf24", fontWeight: "bold" }}>
                                            {formatCurrency(row.value_aed, row.display_currency || tableCurrency)}
                                        </td>
                                        {showMargin && (
                                            <td style={{ padding: "8px 8px", paddingLeft: "30px", textAlign: "left", color: row.margin_pct != null ? (row.margin_pct >= 25 ? "#22c55e" : row.margin_pct >= 20 ? "#fbbf24" : "#ef4444") : "#94a3b8", fontWeight: "600" }}>
                                                {row.margin_pct != null ? `${row.margin_pct.toFixed(1)}%` : "-"}
                                            </td>
                                        )}
                                        <td style={{ padding: "8px 8px", paddingLeft: showMargin ? "60px" : "30px", whiteSpace: "nowrap" }}>
                                            {formatDate(row.close_date)}
                                        </td>
                                        <td style={{ padding: "8px 8px", paddingLeft: showMargin ? "60px" : "30px", whiteSpace: "nowrap" }}>
                                            {row.br_company || "-"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }, [fullScreen, getThStyle, getSortIcon, showMargin, currency, displayCurrency]);

    const handleRetry = useCallback(() => {
        setError("");
        if (salesperson && year) {
            fetchData();
        }
    }, [salesperson, year, fetchData]);

    const handleMaximize = useCallback(() => {
        if (onMaximize) onMaximize();
    }, [onMaximize]);

    if (!salesperson || !year) return null;

    return (
        <div style={{
            border: fullScreen ? "none" : `2px solid ${theme.colors.border.default}`,
            padding: fullScreen ? "0" : theme.spacing.lg + " " + theme.spacing.xl,
            borderRadius: fullScreen ? "0" : theme.borderRadius.lg,
            background: fullScreen ? "transparent" : theme.colors.gradient.cardDark,
            boxShadow: fullScreen ? "none" : theme.shadows.lg,
            marginBottom: fullScreen ? "0" : "32px",
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            transition: "all 0.3s ease",
            position: "relative",
            overflow: "hidden",
        }}>
            {/* Header */}
            <FOCHeader year={year} fullScreen={fullScreen} onMaximize={handleMaximize} />

            <FOCSubTitle salesperson={salesperson} region={region} fullScreen={fullScreen} />


            {loading && <SkeletonCard height="300px" />}

            <FOCErrorBox error={error} onRetry={handleRetry} />

            {
                !loading && !error && !hasData && (
                    <EmptyState
                        icon=""
                        message="No FOC Sample deals found for the selected filters. Try selecting a different year or region."
                        size="medium"
                    />
                )
            }

            {
                !loading && !error && hasData && (
                    <div style={{
                        display: "flex",
                        gap: "24px",
                        alignItems: "stretch",
                        marginTop: 8,
                        flex: 1,
                        minHeight: 0,
                    }}>
                        {/* Closed Won Section - Left */}
                        {renderTable(
                            sortedWonRows,
                            sortConfigWon,
                            handleSortWon,
                            "Closed Won",
                            theme.colors.success
                        )}

                        {/* Closed Lost Section - Right */}
                        {renderTable(
                            sortedLostRows,
                            sortConfigLost,
                            handleSortLost,
                            "Closed Lost",
                            theme.colors.danger
                        )}
                    </div>
                )
            }
        </div >
    );
}

export default FOCSampleTracker;
